<template>
  <div>
    <div class="overlay"  v-if="loadingPagination || externalLoadingPagination">
      <v-progress-circular size="200" :width="8" indeterminate>
        <span class="loading-text">Loading...</span>
      </v-progress-circular>
    </div>
    <table class="main-data-table" :class="{ 'loading': loadingPagination || externalLoadingPagination }">
      <colgroup>
        <col
          v-for="header in display_headers"
          :key="header.text"
          :align="header.align || 'left'"
          :style="{
            width: header.width || 'auto',
          }"
        />
      </colgroup>
      <thead>
        <tr>
          <th :colspan="display_headers.length" class="caption caption-border">
            {{ title }}
            <span class="count">
              {{ title ? "(" : "" }}
              <span v-if="this.value.length == 0"> No items to display </span>
              <span v-else>
                Showing items {{ pagination_range_start + 1 }}-{{ pagination_range_end }} out of
                {{ backendPagination ? pagination.total : this.value.length }} items
              </span>
              {{ title ? ")" : "" }}
            </span>
            <span class="count">
              <slot :name="'header_totals'"></slot>
            </span>
            <span class="count">
              <strong>. On Page:</strong>
              <span> {{ page }}</span>
            </span>
          </th>
        </tr>
        <tr>
          <th :colspan="display_headers.length" class="caption">
            <span v-if="filterable" class="action">
              <validated-text-input v-model="search_query" label="Search...." class="placeholder" />
            </span>
            <span class="action" v-if="editableColumns">
              <md-field>
                <label for="show_columns">Show Columns</label>
                <md-select v-model="headers_shown" name="show_columns" multiple>
                  <md-option
                    v-for="header in headers"
                    :key="header.value"
                    :value="header.value"
                    :disabled="header.optional == false"
                    >
                    {{ header.text }}
                  </md-option>
                </md-select>
              </md-field>
            </span>
            <span class="action">
              <md-button @click="exportCSV" class="md-button md-primary" v-if="canExport">
                <md-icon>file_download</md-icon>
                Export
                <md-tooltip md-direction="top">Export To CSV</md-tooltip>
              </md-button>
            </span>
            <div class="md-layout paging-header">
                <!-- Start -->
                <md-button
                  @click="goToPage(1)"
                  class="md-icon-button md-primary md-simple"
                  :disabled="page <= 1"
                >
                  <md-icon>first_page</md-icon>
                  <md-tooltip md-direction="top">First page</md-tooltip>
                </md-button>

                <!-- Backward -->
                <md-button
                  @click="previousPage"
                  class="md-icon-button md-primary md-simple"
                  :disabled="page <= 1"
                >
                  <md-icon>navigate_before</md-icon>
                  <md-tooltip md-direction="top">Previous page</md-tooltip>
                </md-button>

                <!-- Page Selector -->
                <validated-select-input
                  class="narrow"
                  label="Page #"
                  v-model="page"
                  :options="pages"
                  :is_sortable="false"
                  @input="handlePageChange()"
                />

                <!-- Forwards -->
                <md-button
                  @click="nextPage"
                  class="md-icon-button md-primary md-simple"
                  :disabled="page >= page_count"
                >
                  <md-icon>navigate_next</md-icon>
                  <md-tooltip md-direction="top">Next page</md-tooltip>
                </md-button>

                <!-- End -->
                <md-button
                  @click="goToPage(-1)"
                  class="md-icon-button md-primary md-simple"
                  :disabled="page >= page_count"
                >
                  <md-icon>last_page</md-icon>
                  <md-tooltip md-direction="top">Last page</md-tooltip>
                </md-button>

                <!-- Page Size Selector -->
                <validated-select-input
                  class="narrow"
                  label="# Per Page"
                  v-model="items_per_page"
                  :options="items_per_page_options"
                  :is_sortable="false"
                  @input="handlePageChange()"
                />
            </div>
          </th>
        </tr>
        <tr :class="[stickyheader ? 'sticky-top':'', 'sticky-background',]">
          <th v-for="header in display_headers"
            :key="header.text" @click="onHeaderItemClick(header)"
            :class="{sortable: header.sort !== undefined}">
            <code class="text-wrap">
              {{ header.text }}
            </code>
            <code v-if="header.sort > 0 || header.sort < 0"><span :class="{sorted: header.sort < 0}">&#8595;</span><span :class="{sorted: header.sort > 0}">&#8593;</span></code>
            <code v-else-if="header.sort !== undefined">↓↑</code>
            <code v-else>&nbsp;&nbsp;</code>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(item, index) of display_items"
          :key="item.id"
          @dblclick="onDoubleClickItem(item, index)"
          :class="item.tr_class"
        >
          <td
            v-for="header in display_headers"
            :key="header.text"
            :class="[
              { 'single-line': header.single_line },
              header.classFunction ? header.classFunction(item) : [],
            ]"
          >
            <slot :name="'item.' + header.value" v-bind:item="item" v-bind:index="index">
              {{ getItemValue(header, item) | filter(header.filter, header.filter_args) }}
            </slot>

          </td>
        </tr>
      </tbody>
      <tfoot>
        <tr v-if="display_items.length == 0">
          <td :colspan="display_headers.length">
            <center style="opacity: 0.5">No items to display</center>
          </td>
        </tr>
        <tr v-if="display_items.length > 0">
          <td :colspan="display_headers.length">
            <div class="md-layout footer">
                <!-- Start -->
                <md-button
                  @click="goToPage(1)"
                  class="md-icon-button md-primary md-simple"
                  :disabled="page <= 1"
                >
                  <md-icon>first_page</md-icon>
                  <md-tooltip md-direction="top">First page</md-tooltip>
                </md-button>

                <!-- Backward -->
                <md-button
                  @click="previousPage"
                  class="md-icon-button md-primary md-simple"
                  :disabled="page <= 1"
                >
                  <md-icon>navigate_before</md-icon>
                  <md-tooltip md-direction="top">Previous page</md-tooltip>
                </md-button>

                <!-- Page Selector -->
                <validated-select-input
                  class="narrow"
                  label="Page #"
                  v-model="page"
                  :options="pages"
                  :is_sortable="false"
                  @input="handlePageChange()"
                />

                <!-- Forwards -->
                <md-button
                  @click="nextPage"
                  class="md-icon-button md-primary md-simple"
                  :disabled="page >= page_count"
                >
                  <md-icon>navigate_next</md-icon>
                  <md-tooltip md-direction="top">Next page</md-tooltip>
                </md-button>

                <!-- End -->
                <md-button
                  @click="goToPage(-1)"
                  class="md-icon-button md-primary md-simple"
                  :disabled="page >= page_count"
                >
                  <md-icon>last_page</md-icon>
                  <md-tooltip md-direction="top">Last page</md-tooltip>
                </md-button>

                <!-- Page Size Selector -->
                <validated-select-input
                  class="narrow"
                  label="# Per Page"
                  v-model="items_per_page"
                  :options="items_per_page_options"
                  :is_sortable="false"
                  @input="handlePageChange()"
                />
            </div>
          </td>
        </tr>
      </tfoot>
    </table>
  </div>
</template>
<script>
import ValidatedInputs from "@/components/ValidatedInput";

export default {
  components: {
    ...ValidatedInputs,
  },
  data() {
    return {
      loadingPagination: false,
      page: 1,
      items_per_page: 50,
      search_query: "",
      headers_shown: [],
      debounceTimer: null,
    };
  },
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    title: {
      type: String,
      default: "",
    },
    headers: {
      type: Array,
      default: () => [],
    },
    stickyheader: {
      type: Array|Boolean,
      default: () => [],
    },
    loading: {
      type: null,
      default: () => null,
    },
    multiSort: {
      type: Boolean,
      default: false,
    },
    itemsPerPage: {
      type: Number,
      default: null,
    },
    filterable: {
      type: Boolean,
      default: false,
    },
    editableColumns: {
      type: Boolean,
      default: false,
    },
    canExport:{
      type: Boolean,
      default: false,
    },
    backendPagination: {
      type: Boolean,
      default: false,
    },
    externalDataRefresh: {
      type: Boolean,
      default: false
    },
    externalLoadingPagination: {
      type: Boolean,
      default: false
    }
  },
  watch: {
    itemsPerPage(count) {
      if (this.backendPagination) {
        this.items_per_page = this.pagination.per_page;
      } else {
        this.items_per_page = count;
      }
    },
    page_count(count) {
      if (this.page > count) {
        this.page = count;
      }
    },
    headers: {
      handler(headers) {
        this.headers_shown = headers.map(h => h.value);
      },
      immediate: true,
    },
    display_items(newValue) {
        this.$emit("visibleItemsChange",newValue);
    },
  },
  computed: {
    display_items() {
      let items = [...this.value];

      //search items
      let search_headers = this.headers.filter((h) => !!h.search);
      if (this.search_query.length > 0 && this.filterable) {
        items = items.filter((item) =>
          search_headers.some((header) => this.searchItem(header, item))
        );
      }

      // sort all items
      let sorting_headers = this.headers.filter((h) => !!h.sort);
      sorting_headers = sorting_headers.sort((h1, h2) => Math.abs(h1.sort) - Math.abs(h2.sort));
      items.sort((item1, item2) => {
        for (let header of sorting_headers) {
          let comparison = this.compareItems(header, item1, item2);
          if (comparison != 0) {
            return comparison;
          }
        }
        return 0;
      });

      if (this.backendPagination) {
        return items;
      } else {
        // paginate, extract the current page from the full array
        return items.slice(this.pagination_range_start, this.pagination_range_end);
      }
    },
    display_headers() {
      return this.headers.filter(header => this.headers_shown.includes(header.value));
    },
    is_paginated() {
      return this.items_per_page && Number(this.items_per_page);
    },
    pagination() {
      return this.$store.state.pagination;
    },
    pagination_range_start() {
      if (this.backendPagination) {
        return (this.pagination.page - 1) * this.pagination.per_page;
      } else if (this.is_paginated) {
        return this.items_per_page * (this.page - 1);
      } else {
        return 0;
      }
    },
    pagination_range_end() {
      if (this.backendPagination) {
        return Math.min(this.pagination.per_page * this.pagination.page, this.pagination.total);
      } else if (this.is_paginated) {
        return Math.min(this.items_per_page * this.page, this.value.length);
      } else {
        return this.value.length;
      }
    },
    page_count() {
      if (this.backendPagination) {
        return Math.ceil(this.pagination.total / this.pagination.per_page) || 1;
      } else if (this.value && this.is_paginated) {
        let count = Math.ceil(this.value.length / this.items_per_page);
        return count || 1;
      } else {
        return 1;
      }
    },
    pages() {
      return Array.from({ length: this.page_count }, (_, index) => index + 1);
    },
    items_per_page_options() {
      let options = [10, 50, 100, 500, 1000];

      if (this.itemsPerPage && !options.includes(this.itemsPerPage)) {
        options.push(this.itemsPerPage);
      }

      options.sort((a, b) => (a > b ? 1 : a < b ? -1 : 0));
      return options;
    },
  },
  methods: {
    onDoubleClickItem(item, index) {
      this.$emit("double-click", item);
    },
    exportCSV(){
      this.$emit("exportCSV");
    },
    onHeaderItemClick(header) {
      // if no sort defined, setting/changing it is pointless as vue has not made it reactive
      // thus not causing a re-sort or rerender, inorder to enable sorting for a header,
      // teh headers prop entry bust have sort: false|Number defined
      if (header.sort == undefined) {
        return;
      }

      let previous_sort = header.sort;
      if (this.multiSort) {
        // multi sort not yet supported
      } else {
        for (let header of this.headers) {
          if (header.sort !== undefined) {
            header.sort = false;
          }
        }
      }

      switch (previous_sort) {
        case false:
          header.sort = -1;
          break;
        case -1:
          header.sort = +1;
          break;
        case +1:
          header.sort = -1;
          break;
      }
    },
    getItemValue(header, item) {
      let valueFunction = header.valueFunction || ((i) => i[header.value]);
      let value = valueFunction(item);

      if (header.type) {
        value = header.type(value);
      }

      return value;
    },
    compareItems(header, item1, item2) {
      let value1 = this.getItemValue(header, item1);
      let value2 = this.getItemValue(header, item2);
      let sortFunction = (value1, value2) => {
        if (header.inputtype == "text") {
          value1 = value1 != null ? value1 : '';
          value2 = value2 != null ? value2 : '';
          if (String(value1) === String(value2)) {
            return 0;
          } else if (String(value1).toLocaleLowerCase()[0] === String(value2).toLocaleLowerCase()[0] && String(value1)[0] !== String(value2)[0]) {
            return String(value1)[0] < String(value2)[0] ? 1 : -1;
          } else if (String(value1).toLocaleLowerCase() === String(value2).toLocaleLowerCase()) {
            return 0;
          } else {
            return String(value1).toLocaleLowerCase() > String(value2).toLocaleLowerCase() ? 1 : -1;
          }
        } else if(header.inputtype == "date") {
            let value1date = value1.split("/").join("-");
            value1date = value1date.split("-");
            let value1timestamp = new Date(parseInt(value1date[2], 10), parseInt(value1date[1], 10) - 1, parseInt(value1date[0]), 10).getTime();

            let value2date = value2.split("/").join("-");
            value2date = value2date.split("-");
            let value2timestamp = new Date(parseInt(value2date[2], 10), parseInt(value2date[1], 10) - 1, parseInt(value2date[0]), 10).getTime();

            value1timestamp = !isNaN(value1timestamp) ? value1timestamp : 0;
            value2timestamp = !isNaN(value2timestamp) ? value2timestamp : 0;
            if (value1timestamp > value2timestamp) {
              return 1;
            } else {
              return -1;
            }
        } else if (value1 == value2) {
          return 0;
        } else {
          return value1 > value2 ? 1 : -1;
        }
      };
      if (header.sortFunction) {
        sortFunction = header.sortFunction;
      }
      return header.sort * sortFunction(value1, value2);
    },
    searchItem(header, item) {
      let value = this.getItemValue(header, item);
      if (String(value).toLocaleLowerCase().indexOf(this.search_query.toLocaleLowerCase()) > -1) {
        return true;
      }
      return false;
    },
    previousPage() {
      if (this.page > 1) {
        this.page -= 1;
      }
    },
    nextPage() {
      if (this.page < this.page_count) {
        this.page += 1;
      }
    },
    goToPage(page) {
      if (page < 1) {
        page = this.page_count + page + 1;
      }
      this.page = page;
    },
    async handlePageChange() {
      if (this.backendPagination) {
        if (this.externalDataRefresh) {
          this.loadingPagination = true;
          await this.$store.commit('set_pagination', {
            ...this.$store.state.pagination,
            page: this.page,
            per_page: this.items_per_page,
          });
          this.$emit('refresh-data');

          this.loadingPagination = false;
        } else {
          if (this.debounceTimer) {
            clearTimeout(this.debounceTimer);
          }

          this.debounceTimer = setTimeout(async () => {
            this.loadingPagination = true;
            await this.$store.commit('set_pagination', {
              ...this.$store.state.pagination,
              page: this.page,
              per_page: this.items_per_page,
            });

            await this.$store.dispatch('fetch_work_basket');
            this.loadingPagination = false;
          }, 300);
        }
      }
    }
  },
  filters: {
    filter(value, filter, args) {
      if (filter) {
        if (args == null) {
          args = [];
        }
        return filter(value, ...args);
      } else {
        return value;
      }
    },
  },
  created() {
    if(!this.backendPagination) {
      this.items_per_page = this.itemsPerPage;
    }
  },
};
</script>
<style scoped lang="scss">
@import "@/assets/scss/theme.scss";

.overlay {
  position: absolute;
  top: 50%;
  left: 50%;
}

.loading-text {
  font-size: 16px;
  font-weight: bold;
  animation: pulse 1.5s infinite ease-in-out;
}

.main-data-table.loading {
  opacity: 0.5;
  pointer-events: none;
}

.main-data-table {
  font-size: 13px;
  table-layout: fixed;
  width: 100%;
  border-collapse: separate;
  border-spacing: 0px;
  border-radius: 5px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  & caption,
  & .caption {
    padding: 8px 16px;
    font-weight: bold;
    text-align: start;
    background-color: #fff !important;

    & .count {
      font-weight: normal;
    }

    & .action {
      float: right;
      margin-left: 15px;
    }

    & .paging-header {
      float: left;
      max-width: 50% !important;
    }
  }

  & > tbody tr:nth-child(even) {
    background-color: rgba(0, 0, 0, 0.05);
  }

  & > tbody tr:hover {
    background-color: rgba(0, 0, 0, 0.1);
  }

  & > tbody tr.bg-white {
    background-color: #fff !important;
  }

  & > tbody tr.bg-blue {
    background-color: $ausloans-blue-o40 !important;
  }
  & > tbody tr.bg-orange-light {
    background-color: $ausloans-orange-light !important;
  }
  & > tbody tr.bg-yellow-light {
    background-color: $ausloans-yellow-light !important;
  }
  & > tbody tr.bg-green-light {
    background-color: $ausloans-green-light !important;
  }
  & > tbody tr.bg-red-light {
    background-color: $ausloans-red-light !important;
  }

  i.voi_gold {
    color: DarkGoldenRod;
  }

  i.voi_silver {
    color: Silver;
  }

  i.voi_bronze {
    color: #cd7f32;
  }


  & > tbody td.single-line {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  & thead tr {
    background: rgba(0, 0, 0, 0.05);
    border-radius: 5px;
  }
  & thead tr:first-child {
    background-color: rgba(255, 255, 255, 1);
  }

  & thead tr th {
    color: $color-text-light;
    word-break: break-word;

    &.sortable {
      cursor: pointer;
    }
    & code:nth-child(2) {
      font-size: medium;
    }
    & span.sorted{
      font-weight: bold;
      color: blue;
      text-shadow: 0px 1px blue;
    }
  }

  & thead tr th code {
    font-family: "Roboto", "Helvetica", "Arial", sans-serif;
  }

  & thead tr th,
  & tbody tr td {
    padding: 5px 8px;
    text-align: start;
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);

    &:not(:first-child) {
      border-left: 1px solid rgba(0, 0, 0, 0.1);
    }

    &.right {
      text-align: end;
    }
  }

  & tfoot {
    .footer {
      padding-top: 10px;
      padding-bottom: 10px;

      & > .narrow {
        max-width: 100px;
      }
    }
  }

  &.dense thead tr th,
  &.dense tbody tr td {
    padding: 5px 5px;
  }

  & thead tr th.text-center,
  & tbody tr td.text-center {
    text-align: center;
  }
  .header-totals{
    margin-top : 15px;
    margin-right:25px;
    span {
        margin: 5px 20px;
    }
  }
}

.orange-icon {
  color: $ausloans-orange !important;
}
.sticky-background {
  background-color: $grey-100 !important;
}

.caption-border {
  border-bottom-color: #fff !important;
}

th,
th code {
  background-color: transparent !important;
  font-family: Roboto, Helvetica, Arial, sans-serif !important;
  font-size: 13px !important;
  font-weight: bold;
}
</style>
